import { useEffect, useState } from "react";
import { Button, ButtonGroup, Checkbox, Dropdown, Form, FormButton, FormField, Message, Segment } from "semantic-ui-react";
import { IOutreachRule, IOutreachRuleSubmission, IProjectStage } from "../../app/interfaces/IOutreachRule";
import { ICompanyEmployee } from "../../slices/companyEmployeesSlice";

interface IOutreachRuleFormProps {
    rule?: IOutreachRule;
    stages: Array<IProjectStage>;
    employees: Array<ICompanyEmployee>;
    // Set on a project page, where every rule this form makes belongs to that
    // project. Left out on the company page, where rules cover all projects.
    projectId?: number;
    // Statuses that already have a rule at this scope, so the form does not
    // offer a duplicate the server would only refuse.
    takenStageIds: Array<number>;
    cancel: () => void;
    save: (rule: IOutreachRuleSubmission) => void;
}

const OutreachRuleForm = ({ rule, stages, employees, projectId, takenStageIds, cancel, save }: IOutreachRuleFormProps) => {
    const [stageId, setStageId] = useState<number | undefined>(rule?.stageId);
    const [recipientIds, setRecipientIds] = useState<Array<number>>(rule ? rule.recipients.map(r => r.id) : []);
    const [notifyAllEmployees, setNotifyAllEmployees] = useState<boolean>(rule ? rule.notifyAllEmployees : false);
    const [isActive, setIsActive] = useState<boolean>(rule ? rule.isActive : true);
    const [formError, setFormError] = useState<string | undefined>(undefined);

    const availableStages = stages.filter(stage => stage.id === rule?.stageId || !takenStageIds.includes(stage.id));

    const onSave = () => {
        if (!stageId) {
            setFormError('Choose the project status that should trigger this reminder.');
            return;
        }
        if (!notifyAllEmployees && !recipientIds.length) {
            setFormError('Choose who to remind, or send it to everyone.');
            return;
        }

        save({
            stageId,
            projectId: projectId ?? null,
            isActive,
            notifyAllEmployees,
            recipientIds: notifyAllEmployees ? [] : recipientIds,
        });
    };

    useEffect(() => {
        if (formError && stageId && (notifyAllEmployees || recipientIds.length)) {
            setFormError(undefined);
        }
    }, [stageId, recipientIds, notifyAllEmployees]);

    return (
        /*
        * The fields have to sit inside a Form: FormField's layout comes from
        * `.ui.form .field`, so without one the labels and controls collapse
        * into a row and the dropdowns get squeezed to nothing. A plain Segment
        * rather than StyledSegment for the same reason - that one is a flex
        * row, which is right for the rule list and wrong for a stacked form.
        */
        <Segment id='outreach-rule-form'>
            <Form>
                <FormField>
                    {/*
                    * The label goes here rather than in a `label` prop:
                    * FormField renders only the label when given one without a
                    * `control`, and throws its children away - which is how
                    * this dropdown came to not exist at all.
                    */}
                    <label>When a project moves to</label>
                    <Dropdown
                        fluid
                        selection
                        placeholder='Project status'
                        value={stageId}
                        options={availableStages.map(stage => ({ key: stage.id, value: stage.id, text: stage.name }))}
                        onChange={(e, x) => setStageId(x.value as number)}
                    />
                </FormField>

                <FormField>
                    <Checkbox
                        label='Remind everyone at the company'
                        checked={notifyAllEmployees}
                        onChange={() => setNotifyAllEmployees(!notifyAllEmployees)}
                    />
                </FormField>

                {
                    !notifyAllEmployees &&
                    <FormField>
                        <label>Remind</label>
                        <Dropdown
                            fluid
                            multiple
                            search
                            selection
                            placeholder='Choose employees'
                            value={recipientIds}
                            options={employees.map(employee => ({
                                key: employee.id,
                                value: employee.id,
                                text: `${employee.firstName} ${employee.lastName}`,
                                description: employee.email,
                            }))}
                            onChange={(e, x) => setRecipientIds(x.value as Array<number>)}
                        />
                    </FormField>
                }

                <FormField>
                    <Checkbox
                        label={projectId
                            ? 'Rule is on for this project (turn off to stop reminders this project would otherwise get)'
                            : 'Rule is on'}
                        checked={isActive}
                        onChange={() => setIsActive(!isActive)}
                    />
                </FormField>

                { formError && <Message negative size='tiny' content={formError} /> }

                <ButtonGroup basic>
                    <FormButton positive icon='check' onClick={onSave} floated='right' />
                    <Button negative icon='close' onClick={cancel} />
                </ButtonGroup>
            </Form>
        </Segment>
    );
};

export default OutreachRuleForm;
